import React, { useState, useEffect } from 'react';
import { 
  Save, RefreshCw, DollarSign, Zap, Sun, Calculator,
  Phone, Mail, Globe, AlertCircle, CheckCircle, Percent,
  Leaf, Shield, TrendingUp, Package, Battery
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const SettingsPage = () => {
  const { fetchWithAuth, hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [settings, setSettings] = useState({
    // Valores monetarios
    dollar_rate: 7500,
    
    // Tarifas ANDE
    ande_kwh_rate_residential: 680,
    ande_kwh_rate_commercial: 750,
    ande_kwh_rate_industrial: 650,
    
    // Costos de instalación
    cost_per_watt_residential: 8500,
    cost_per_watt_commercial: 7800,
    cost_per_watt_industrial: 7200,
    
    // Precios de equipos - COSTO (USD)
    panel_price_usd: 85,
    inverter_price_usd: 2550,
    inverter_price_per_kw_usd: 510,
    structure_price_set_usd: 7000,
    structure_price_per_panel_usd: 47,
    cable_pv_price_per_meter_usd: 0.80,
    installation_labor_per_kw_usd: 100,
    cabling_accessories_percent: 8,
    battery_price_per_kwh_usd: 260,
    battery_unit_price_usd: 1300,
    
    // Márgenes de ganancia
    margin_contingency_percent: 5,
    margin_gross_profit_percent: 25,
    margin_net_target_percent: 15,
    
    // Parámetros solares
    solar_hours_per_day: 5.0,
    solar_panel_default_watts: 740,
    solar_efficiency_factor: 0.85,
    system_degradation_year: 0.5,
    panel_warranty_years: 25,
    inverter_warranty_years: 10,
    
    // Descuentos
    cash_discount_percent: 5,
    volume_discount_threshold_kw: 10,
    volume_discount_percent: 3,
    referral_discount_percent: 2,
    
    // Financiamiento
    financing_max_months: 24,
    financing_min_months: 6,
    financing_interest_rate: 0,
    energy_price_increase_year: 8,
    
    // NUEVO: Parámetros avanzados de calculadora
    system_lifetime_years: 25,
    annual_degradation_rate: 0.5,
    annual_maintenance_cost_usd: 50,
    maintenance_includes_cleaning: true,
    government_incentive_percent: 0,
    tax_credit_percent: 0,
    inverter_efficiency: 0.97,
    system_losses_percent: 14,
    currency_symbol: 'Gs.',
    currency_code: 'PYG',
    default_sun_hours: 5.0,
    allow_manual_sun_hours: true,
    min_sun_hours: 3.5,
    max_sun_hours: 6.5,
    
    // Impacto ambiental
    carbon_offset_per_kw: 1200,
    trees_equivalent_per_kw: 40,
    water_saved_liters_per_kw: 1500,
    
    // Beneficios fiscales
    iva_exento: true,
    deduccion_renta_percent: 100,
    
    // Contacto
    whatsapp_number: '+595991344956',
    contact_email: 'zenexparaguay@gmail.com',
    contact_phone: '+595 21 XXX XXXX',
    
    // Redes sociales
    facebook_url: '',
    instagram_url: '',
    linkedin_url: '',
    youtube_url: ''
  });

  const canEdit = hasPermission('settings_edit');

  // Función para calcular costos de un sistema
  const calculateSystemCost = (numPanels) => {
    const panelWatts = 740;
    const capacityKw = (numPanels * panelWatts) / 1000;
    
    // Costos base en USD
    const panelsCost = numPanels * (settings.panel_price_usd || 85);
    const inverterCost = capacityKw * (settings.inverter_price_per_kw_usd || 510);
    const structureCost = numPanels * (settings.structure_price_per_panel_usd || 47);
    const laborCost = capacityKw * (settings.installation_labor_per_kw_usd || 100);
    const subtotal = panelsCost + inverterCost + structureCost + laborCost;
    const cablingCost = subtotal * ((settings.cabling_accessories_percent || 8) / 100);
    const baseCostUsd = subtotal + cablingCost;
    
    // Aplicar márgenes
    const contingencyPercent = settings.margin_contingency_percent || 5;
    const grossMarginPercent = settings.margin_gross_profit_percent || 25;
    const withContingency = baseCostUsd * (1 + contingencyPercent / 100);
    const finalPriceUsd = withContingency * (1 + grossMarginPercent / 100);
    
    // Convertir a Guaraníes
    const dollarRate = settings.dollar_rate || 7500;
    const finalPriceGs = finalPriceUsd * dollarRate;
    
    // Ganancia
    const profitUsd = finalPriceUsd - baseCostUsd;
    const profitGs = profitUsd * dollarRate;
    
    return {
      numPanels,
      capacityKw: capacityKw.toFixed(2),
      panelsCost,
      inverterCost,
      structureCost,
      laborCost,
      cablingCost: cablingCost.toFixed(0),
      baseCostUsd: baseCostUsd.toFixed(0),
      withContingencyUsd: withContingency.toFixed(0),
      finalPriceUsd: finalPriceUsd.toFixed(0),
      finalPriceGs: Math.round(finalPriceGs),
      profitUsd: profitUsd.toFixed(0),
      profitGs: Math.round(profitGs),
      profitPercent: ((profitUsd / baseCostUsd) * 100).toFixed(1)
    };
  };

  // Ejemplos de sistemas para la tabla
  const systemExamples = [4, 6, 8, 10, 15, 20].map(calculateSystemCost);

  // Formatear moneda
  const formatGs = (num) => new Intl.NumberFormat('es-PY').format(num);
  const formatUsd = (num) => new Intl.NumberFormat('en-US').format(num);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth('/api/settings');
      if (res.ok) {
        const data = await res.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Error cargando configuración:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    
    try {
      const res = await fetchWithAuth('/api/settings', {
        method: 'PUT',
        body: JSON.stringify(settings)
      });

      if (res.ok) {
        setMessage({ type: 'success', text: 'Configuración guardada exitosamente' });
      } else {
        const error = await res.json();
        setMessage({ type: 'error', text: error.detail || 'Error al guardar' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error de conexión' });
    }
    setSaving(false);
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const InputField = ({ label, field, type = 'text', prefix, suffix, step, min, description }) => (
    <div>
      <label className="block text-sm font-medium text-zinc-400 mb-1">{label}</label>
      {description && <p className="text-xs text-zinc-500 mb-1.5">{description}</p>}
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">{prefix}</span>
        )}
        <input
          type={type}
          value={settings[field] ?? ''}
          onChange={(e) => handleChange(field, type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
          disabled={!canEdit}
          step={step}
          min={min}
          className={`w-full bg-zinc-800/50 border border-zinc-700 rounded-lg py-2 text-white text-sm
            ${prefix ? 'pl-10' : 'pl-3'} ${suffix ? 'pr-14' : 'pr-3'}
            focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 
            disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 text-xs">{suffix}</span>
        )}
      </div>
    </div>
  );

  const CheckboxField = ({ label, field, description }) => (
    <div className="flex items-start gap-3">
      <input
        type="checkbox"
        checked={settings[field] || false}
        onChange={(e) => handleChange(field, e.target.checked)}
        disabled={!canEdit}
        className="mt-1 w-4 h-4 rounded border-zinc-600 text-emerald-500 focus:ring-emerald-500 bg-zinc-800"
      />
      <div>
        <label className="text-sm font-medium text-zinc-300">{label}</label>
        {description && <p className="text-xs text-zinc-500">{description}</p>}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Configuración del Sistema</h1>
          <p className="text-zinc-400 text-sm">Variables para la calculadora solar y datos de contacto</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={loadSettings}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Recargar
          </button>
          {canEdit && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors disabled:opacity-50"
              data-testid="save-settings-btn"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          )}
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${
          message.type === 'success' 
            ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400' 
            : 'bg-red-500/10 border border-red-500/30 text-red-400'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      {/* Settings Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        
        {/* Valores Monetarios */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
          <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-amber-400" />
            Valores Monetarios
          </h3>
          <div className="space-y-3">
            <InputField 
              label="Valor del Dólar" 
              field="dollar_rate" 
              type="number"
              suffix="Gs/USD"
            />
          </div>
        </div>

        {/* Tarifas ANDE */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
          <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-400" />
            Tarifas ANDE
          </h3>
          <div className="space-y-3">
            <InputField label="Residencial" field="ande_kwh_rate_residential" type="number" suffix="Gs/kWh" />
            <InputField label="Comercial" field="ande_kwh_rate_commercial" type="number" suffix="Gs/kWh" />
            <InputField label="Industrial" field="ande_kwh_rate_industrial" type="number" suffix="Gs/kWh" />
          </div>
        </div>

        {/* Márgenes de Ganancia */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 border-amber-500/30">
          <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-amber-400" />
            Márgenes de Ganancia
          </h3>
          <div className="space-y-3">
            <InputField 
              label="Margen de Contingencia" 
              field="margin_contingency_percent" 
              type="number" 
              suffix="%" 
              step="0.5"
              description="Para variaciones (5%)"
            />
            <InputField 
              label="Margen Bruto de Utilidad" 
              field="margin_gross_profit_percent" 
              type="number" 
              suffix="%" 
              step="1"
              description="Sobre costos (20-30%)"
            />
          </div>
        </div>
      </div>

      {/* ============== SECCIÓN DE PRECIOS UNITARIOS POR PRODUCTO ============== */}
      <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 border border-zinc-700 rounded-xl p-6">
        <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-3">
          <Package className="w-7 h-7 text-emerald-400" />
          Catálogo de Precios por Producto
        </h3>
        <p className="text-sm text-zinc-400 mb-6">Precios de costo (compra) - Editá los valores y se actualizará el simulador automáticamente</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Panel Solar */}
          <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700 hover:border-emerald-500/50 transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                <Sun className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <p className="text-white font-semibold">Panel Solar</p>
                <p className="text-zinc-500 text-xs">740W Alta Eficiencia</p>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs text-zinc-400">Precio por unidad</label>
              <div className="flex items-center gap-2">
                <span className="text-zinc-500">$</span>
                <input
                  type="number"
                  value={settings.panel_price_usd || 85}
                  onChange={(e) => setSettings({...settings, panel_price_usd: parseFloat(e.target.value) || 0})}
                  className="flex-1 bg-zinc-900 border border-zinc-600 rounded-lg px-3 py-2 text-white text-lg font-bold focus:border-emerald-500 focus:outline-none"
                />
                <span className="text-zinc-500">USD</span>
              </div>
              <p className="text-xs text-emerald-400">= Gs. {formatGs(Math.round((settings.panel_price_usd || 85) * (settings.dollar_rate || 7500)))}</p>
            </div>
          </div>

          {/* Inversor */}
          <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700 hover:border-emerald-500/50 transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Zap className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-white font-semibold">Inversor</p>
                <p className="text-zinc-500 text-xs">On-Grid / Híbrido</p>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs text-zinc-400">Precio por kW</label>
              <div className="flex items-center gap-2">
                <span className="text-zinc-500">$</span>
                <input
                  type="number"
                  value={settings.inverter_price_per_kw_usd || 510}
                  onChange={(e) => setSettings({...settings, inverter_price_per_kw_usd: parseFloat(e.target.value) || 0})}
                  className="flex-1 bg-zinc-900 border border-zinc-600 rounded-lg px-3 py-2 text-white text-lg font-bold focus:border-emerald-500 focus:outline-none"
                />
                <span className="text-zinc-500">/kW</span>
              </div>
              <p className="text-xs text-zinc-500">Inversor 5kW ≈ ${formatUsd((settings.inverter_price_per_kw_usd || 510) * 5)}</p>
            </div>
          </div>

          {/* Estructura */}
          <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700 hover:border-emerald-500/50 transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Package className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <p className="text-white font-semibold">Estructura</p>
                <p className="text-zinc-500 text-xs">Montaje y fijación</p>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs text-zinc-400">Precio por panel</label>
              <div className="flex items-center gap-2">
                <span className="text-zinc-500">$</span>
                <input
                  type="number"
                  value={settings.structure_price_per_panel_usd || 47}
                  onChange={(e) => setSettings({...settings, structure_price_per_panel_usd: parseFloat(e.target.value) || 0})}
                  className="flex-1 bg-zinc-900 border border-zinc-600 rounded-lg px-3 py-2 text-white text-lg font-bold focus:border-emerald-500 focus:outline-none"
                />
                <span className="text-zinc-500">USD</span>
              </div>
              <p className="text-xs text-zinc-500">Set 10 paneles ≈ ${formatUsd((settings.structure_price_per_panel_usd || 47) * 10)}</p>
            </div>
          </div>

          {/* Batería */}
          <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700 hover:border-emerald-500/50 transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Battery className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-white font-semibold">Batería Litio</p>
                <p className="text-zinc-500 text-xs">LiFePO4 ~5kWh</p>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs text-zinc-400">Precio por kWh</label>
              <div className="flex items-center gap-2">
                <span className="text-zinc-500">$</span>
                <input
                  type="number"
                  value={settings.battery_price_per_kwh_usd || 260}
                  onChange={(e) => setSettings({...settings, battery_price_per_kwh_usd: parseFloat(e.target.value) || 0})}
                  className="flex-1 bg-zinc-900 border border-zinc-600 rounded-lg px-3 py-2 text-white text-lg font-bold focus:border-emerald-500 focus:outline-none"
                />
                <span className="text-zinc-500">/kWh</span>
              </div>
              <p className="text-xs text-zinc-500">Batería 5kWh ≈ ${formatUsd((settings.battery_price_per_kwh_usd || 260) * 5)}</p>
            </div>
          </div>
        </div>

        {/* Segunda fila de productos */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          {/* Mano de Obra */}
          <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <Calculator className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-white font-semibold">Mano de Obra</p>
                <p className="text-zinc-500 text-xs">Instalación profesional</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-zinc-500">$</span>
              <input
                type="number"
                value={settings.installation_labor_per_kw_usd || 100}
                onChange={(e) => setSettings({...settings, installation_labor_per_kw_usd: parseFloat(e.target.value) || 0})}
                className="flex-1 bg-zinc-900 border border-zinc-600 rounded-lg px-3 py-2 text-white font-bold focus:border-emerald-500 focus:outline-none"
              />
              <span className="text-zinc-500">/kW</span>
            </div>
          </div>

          {/* Cableado */}
          <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                <Percent className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-white font-semibold">Cableado y Accesorios</p>
                <p className="text-zinc-500 text-xs">% sobre subtotal</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.5"
                value={settings.cabling_accessories_percent || 8}
                onChange={(e) => setSettings({...settings, cabling_accessories_percent: parseFloat(e.target.value) || 0})}
                className="flex-1 bg-zinc-900 border border-zinc-600 rounded-lg px-3 py-2 text-white font-bold focus:border-emerald-500 focus:outline-none"
              />
              <span className="text-zinc-500">%</span>
            </div>
          </div>

          {/* Medidor Bidireccional */}
          <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                <Zap className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <p className="text-white font-semibold">Medidor Bidireccional</p>
                <p className="text-zinc-500 text-xs">Para On-Grid (Ley 7599)</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-zinc-500">$</span>
              <input
                type="number"
                value={400}
                disabled
                className="flex-1 bg-zinc-900/50 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-400 font-bold cursor-not-allowed"
              />
              <span className="text-zinc-500">USD</span>
            </div>
          </div>
        </div>
      </div>

      {/* ============== SIMULADOR DE COSTOS POR SISTEMA ============== */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
          <Calculator className="w-6 h-6 text-emerald-400" />
          Simulador de Costos por Sistema
          <span className="ml-2 px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">Se actualiza en tiempo real</span>
        </h3>
        <p className="text-sm text-zinc-400 mb-6">Vista previa de precios según la configuración actual (Paneles 740W)</p>
        
        {/* Tabla de costos */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-700">
                <th className="text-left py-3 px-2 text-zinc-400 font-medium">Sistema</th>
                <th className="text-right py-3 px-2 text-zinc-400 font-medium">Paneles</th>
                <th className="text-right py-3 px-2 text-zinc-400 font-medium">Inversor</th>
                <th className="text-right py-3 px-2 text-zinc-400 font-medium">Estructura</th>
                <th className="text-right py-3 px-2 text-zinc-400 font-medium">M. Obra</th>
                <th className="text-right py-3 px-2 text-zinc-400 font-medium">Cableado</th>
                <th className="text-right py-3 px-2 text-zinc-500 font-medium bg-zinc-800/50">Costo Base</th>
                <th className="text-right py-3 px-2 text-amber-400 font-medium bg-amber-500/10">+ Márgenes</th>
                <th className="text-right py-3 px-2 text-emerald-400 font-medium bg-emerald-500/10">Precio Venta</th>
                <th className="text-right py-3 px-2 text-green-400 font-medium">Ganancia</th>
              </tr>
            </thead>
            <tbody>
              {systemExamples.map((sys, idx) => (
                <tr key={idx} className="border-b border-zinc-800 hover:bg-zinc-800/30 transition-colors">
                  <td className="py-3 px-2">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-bold">{sys.capacityKw} kW</span>
                      <span className="text-zinc-500 text-xs">({sys.numPanels} paneles)</span>
                    </div>
                  </td>
                  <td className="text-right py-3 px-2 text-zinc-300">${formatUsd(sys.panelsCost)}</td>
                  <td className="text-right py-3 px-2 text-zinc-300">${formatUsd(sys.inverterCost)}</td>
                  <td className="text-right py-3 px-2 text-zinc-300">${formatUsd(sys.structureCost)}</td>
                  <td className="text-right py-3 px-2 text-zinc-300">${formatUsd(sys.laborCost)}</td>
                  <td className="text-right py-3 px-2 text-zinc-300">${sys.cablingCost}</td>
                  <td className="text-right py-3 px-2 text-zinc-400 bg-zinc-800/50 font-mono">${formatUsd(sys.baseCostUsd)}</td>
                  <td className="text-right py-3 px-2 text-amber-400 bg-amber-500/10 font-mono">${formatUsd(sys.finalPriceUsd)}</td>
                  <td className="text-right py-3 px-2 bg-emerald-500/10">
                    <div className="text-emerald-400 font-bold">Gs. {formatGs(sys.finalPriceGs)}</div>
                    <div className="text-emerald-600 text-xs">(${formatUsd(sys.finalPriceUsd)} USD)</div>
                  </td>
                  <td className="text-right py-3 px-2">
                    <div className="text-green-400 font-bold">${formatUsd(sys.profitUsd)}</div>
                    <div className="text-green-600 text-xs">+{sys.profitPercent}%</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Leyenda */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-zinc-800/50 rounded-lg p-4">
            <p className="text-xs text-zinc-500 mb-1">COSTO BASE</p>
            <p className="text-sm text-zinc-300">Paneles + Inversor + Estructura + M.Obra + Cableado ({settings.cabling_accessories_percent || 8}%)</p>
          </div>
          <div className="bg-amber-500/10 rounded-lg p-4 border border-amber-500/20">
            <p className="text-xs text-amber-400 mb-1">MÁRGENES APLICADOS</p>
            <p className="text-sm text-amber-300">
              Contingencia: +{settings.margin_contingency_percent || 5}% → Margen Bruto: +{settings.margin_gross_profit_percent || 25}%
            </p>
          </div>
          <div className="bg-emerald-500/10 rounded-lg p-4 border border-emerald-500/20">
            <p className="text-xs text-emerald-400 mb-1">PRECIO DE VENTA</p>
            <p className="text-sm text-emerald-300">
              Precio final al cliente (Tipo de cambio: Gs. {formatGs(settings.dollar_rate || 7500)}/USD)
            </p>
          </div>
        </div>

        {/* Ejemplo detallado */}
        <div className="mt-6 bg-zinc-800/30 rounded-lg p-4 border border-zinc-700">
          <p className="text-sm text-white font-medium mb-3">📊 Ejemplo detallado: Sistema de {systemExamples[1]?.numPanels || 6} paneles ({systemExamples[1]?.capacityKw || '4.44'} kW)</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <p className="text-zinc-500 text-xs">Paneles ({systemExamples[1]?.numPanels || 6} × ${settings.panel_price_usd || 85})</p>
              <p className="text-white font-mono">${formatUsd(systemExamples[1]?.panelsCost || 0)}</p>
            </div>
            <div>
              <p className="text-zinc-500 text-xs">Inversor ({systemExamples[1]?.capacityKw || '4.44'}kW × ${settings.inverter_price_per_kw_usd || 510})</p>
              <p className="text-white font-mono">${formatUsd(systemExamples[1]?.inverterCost || 0)}</p>
            </div>
            <div>
              <p className="text-zinc-500 text-xs">Estructura ({systemExamples[1]?.numPanels || 6} × ${settings.structure_price_per_panel_usd || 47})</p>
              <p className="text-white font-mono">${formatUsd(systemExamples[1]?.structureCost || 0)}</p>
            </div>
            <div>
              <p className="text-zinc-500 text-xs">Mano de obra ({systemExamples[1]?.capacityKw || '4.44'}kW × ${settings.installation_labor_per_kw_usd || 100})</p>
              <p className="text-white font-mono">${formatUsd(systemExamples[1]?.laborCost || 0)}</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-zinc-700 flex flex-wrap gap-6">
            <div>
              <p className="text-zinc-500 text-xs">COSTO BASE USD</p>
              <p className="text-zinc-300 font-mono text-lg">${formatUsd(systemExamples[1]?.baseCostUsd || 0)}</p>
            </div>
            <div className="text-zinc-500">→</div>
            <div>
              <p className="text-amber-400 text-xs">+ {settings.margin_contingency_percent || 5}% Contingencia + {settings.margin_gross_profit_percent || 25}% Margen</p>
              <p className="text-amber-300 font-mono text-lg">${formatUsd(systemExamples[1]?.finalPriceUsd || 0)}</p>
            </div>
            <div className="text-zinc-500">→</div>
            <div>
              <p className="text-emerald-400 text-xs">PRECIO FINAL (× {formatGs(settings.dollar_rate || 7500)} Gs)</p>
              <p className="text-emerald-400 font-bold text-xl">Gs. {formatGs(systemExamples[1]?.finalPriceGs || 0)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Resto de secciones */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">

        {/* Parámetros Solares */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
          <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
            <Sun className="w-5 h-5 text-orange-400" />
            Parámetros Solares
          </h3>
          <div className="space-y-3">
            <InputField label="Horas pico de sol" field="solar_hours_per_day" type="number" step="0.1" suffix="hrs/día" />
            <InputField label="Potencia panel estándar" field="solar_panel_default_watts" type="number" suffix="W" />
            <InputField label="Factor de eficiencia" field="solar_efficiency_factor" type="number" step="0.01" description="Ej: 0.85 = 85%" />
            <InputField label="Degradación anual" field="system_degradation_year" type="number" step="0.1" suffix="%" />
          </div>
        </div>

        {/* Garantías */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
          <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-400" />
            Garantías
          </h3>
          <div className="space-y-3">
            <InputField label="Garantía paneles" field="panel_warranty_years" type="number" suffix="años" />
            <InputField label="Garantía inversores" field="inverter_warranty_years" type="number" suffix="años" />
          </div>
        </div>

        {/* Descuentos */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
          <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
            <Percent className="w-5 h-5 text-green-400" />
            Descuentos
          </h3>
          <div className="space-y-3">
            <InputField label="Descuento al contado" field="cash_discount_percent" type="number" suffix="%" step="0.5" />
            <InputField label="Umbral descuento volumen" field="volume_discount_threshold_kw" type="number" suffix="kW" description="Sistemas mayores a este tamaño" />
            <InputField label="Descuento por volumen" field="volume_discount_percent" type="number" suffix="%" step="0.5" />
            <InputField label="Descuento por referido" field="referral_discount_percent" type="number" suffix="%" step="0.5" />
          </div>
        </div>

        {/* Financiamiento */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
          <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-400" />
            Financiamiento y Proyecciones
          </h3>
          <div className="space-y-3">
            <InputField label="Mínimo de cuotas" field="financing_min_months" type="number" suffix="meses" />
            <InputField label="Máximo de cuotas" field="financing_max_months" type="number" suffix="meses" />
            <InputField label="Tasa de interés" field="financing_interest_rate" type="number" step="0.1" suffix="%" description="0 = sin intereses" />
            <InputField label="Aumento tarifa eléctrica anual" field="energy_price_increase_year" type="number" step="0.5" suffix="%" description="Para proyecciones a 25 años" />
          </div>
        </div>

        {/* NUEVO: Parámetros Avanzados de Calculadora Solar */}
        <div className="bg-gradient-to-br from-emerald-900/30 to-zinc-900/50 border border-emerald-500/30 rounded-xl p-5 lg:col-span-2 xl:col-span-3">
          <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
            <Calculator className="w-6 h-6 text-emerald-400" />
            Parámetros Avanzados de Calculadora Solar
            <span className="ml-2 px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">ROI & Fórmulas</span>
          </h3>
          <p className="text-sm text-zinc-400 mb-6">Configure los parámetros técnicos que afectan el cálculo de ROI, payback y proyecciones de ahorro de la calculadora pública.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Vida útil y degradación */}
            <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
              <p className="text-xs text-emerald-400 font-bold uppercase tracking-wider mb-3">Vida Útil del Sistema</p>
              <div className="space-y-3">
                <InputField 
                  label="Vida útil del sistema" 
                  field="system_lifetime_years" 
                  type="number" 
                  suffix="años" 
                  description="Para cálculo ROI y ahorro total"
                />
                <InputField 
                  label="Tasa de degradación anual" 
                  field="annual_degradation_rate" 
                  type="number" 
                  step="0.1"
                  suffix="%" 
                  description="Degradación de paneles por año"
                />
              </div>
            </div>

            {/* Costos de mantenimiento */}
            <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
              <p className="text-xs text-amber-400 font-bold uppercase tracking-wider mb-3">Mantenimiento</p>
              <div className="space-y-3">
                <InputField 
                  label="Costo mantenimiento anual" 
                  field="annual_maintenance_cost_usd" 
                  type="number" 
                  step="1"
                  prefix="$"
                  suffix="/kW" 
                  description="Costo anual por kW instalado"
                />
                <CheckboxField 
                  label="Incluye limpieza" 
                  field="maintenance_includes_cleaning" 
                  description="El mantenimiento incluye limpieza de paneles"
                />
              </div>
            </div>

            {/* Incentivos */}
            <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
              <p className="text-xs text-blue-400 font-bold uppercase tracking-wider mb-3">Incentivos y Créditos</p>
              <div className="space-y-3">
                <InputField 
                  label="Incentivo gubernamental" 
                  field="government_incentive_percent" 
                  type="number" 
                  step="1"
                  suffix="%" 
                  description="% de subsidio sobre costo total"
                />
                <InputField 
                  label="Crédito fiscal" 
                  field="tax_credit_percent" 
                  type="number" 
                  step="1"
                  suffix="%" 
                  description="% de crédito fiscal aplicable"
                />
              </div>
            </div>

            {/* Eficiencia del sistema */}
            <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
              <p className="text-xs text-purple-400 font-bold uppercase tracking-wider mb-3">Eficiencia del Sistema</p>
              <div className="space-y-3">
                <InputField 
                  label="Eficiencia del inversor" 
                  field="inverter_efficiency" 
                  type="number" 
                  step="0.01"
                  description="Ej: 0.97 = 97%"
                />
                <InputField 
                  label="Pérdidas del sistema" 
                  field="system_losses_percent" 
                  type="number" 
                  step="0.5"
                  suffix="%" 
                  description="Pérdidas por cableado, temperatura, etc."
                />
              </div>
            </div>
          </div>

          {/* Segunda fila: Horas de sol y moneda */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {/* Horas de sol */}
            <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
              <p className="text-xs text-orange-400 font-bold uppercase tracking-wider mb-3">Configuración de Horas de Sol</p>
              <div className="grid grid-cols-3 gap-3">
                <InputField 
                  label="Horas de sol por defecto" 
                  field="default_sun_hours" 
                  type="number" 
                  step="0.1"
                  suffix="HSP" 
                />
                <InputField 
                  label="Mínimo ajustable" 
                  field="min_sun_hours" 
                  type="number" 
                  step="0.1"
                  suffix="HSP" 
                />
                <InputField 
                  label="Máximo ajustable" 
                  field="max_sun_hours" 
                  type="number" 
                  step="0.1"
                  suffix="HSP" 
                />
              </div>
              <div className="mt-3">
                <CheckboxField 
                  label="Permitir ajuste manual de horas de sol" 
                  field="allow_manual_sun_hours" 
                  description="El usuario puede ajustar las horas de sol en la calculadora"
                />
              </div>
            </div>

            {/* Moneda */}
            <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
              <p className="text-xs text-cyan-400 font-bold uppercase tracking-wider mb-3">Formato de Moneda</p>
              <div className="grid grid-cols-2 gap-3">
                <InputField 
                  label="Símbolo de moneda" 
                  field="currency_symbol" 
                  type="text" 
                  description="Ej: Gs., $, €"
                />
                <InputField 
                  label="Código ISO" 
                  field="currency_code" 
                  type="text" 
                  description="Ej: PYG, USD, EUR"
                />
              </div>
            </div>
          </div>

          {/* Explicación de fórmula ROI */}
          <div className="mt-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
            <p className="text-emerald-400 text-sm font-bold mb-2">Fórmula de ROI utilizada en la calculadora:</p>
            <code className="text-xs text-zinc-300 block bg-zinc-900 rounded p-3 font-mono">
              ROI (%) = ((Ahorro Total en Vida Útil - Costo Total del Sistema) / Costo Total del Sistema) × 100
            </code>
            <p className="text-zinc-500 text-xs mt-2">
              Donde: <br/>
              • <strong>Ahorro Total</strong> = Suma de ahorros anuales considerando degradación de paneles y aumento de tarifas<br/>
              • <strong>Costo Total</strong> = Costo de instalación + Costos de mantenimiento durante vida útil - Incentivos/Créditos fiscales
            </p>
          </div>
        </div>

        {/* Impacto Ambiental */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
          <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
            <Leaf className="w-5 h-5 text-green-500" />
            Impacto Ambiental
          </h3>
          <div className="space-y-3">
            <InputField label="Offset CO₂ por kW" field="carbon_offset_per_kw" type="number" suffix="kg/año" />
            <InputField label="Árboles equivalentes por kW" field="trees_equivalent_per_kw" type="number" suffix="árboles" />
            <InputField label="Agua ahorrada por kW" field="water_saved_liters_per_kw" type="number" suffix="litros/año" />
          </div>
        </div>

        {/* Beneficios Fiscales */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
          <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-cyan-400" />
            Beneficios Fiscales (Ley 6977)
          </h3>
          <div className="space-y-4">
            <CheckboxField 
              label="IVA Exento" 
              field="iva_exento" 
              description="Equipos solares exentos de IVA"
            />
            <InputField 
              label="Deducción de Renta" 
              field="deduccion_renta_percent" 
              type="number" 
              suffix="%" 
              description="Porcentaje deducible del impuesto a la renta"
            />
          </div>
        </div>

        {/* Contacto */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
          <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
            <Phone className="w-5 h-5 text-cyan-400" />
            Datos de Contacto
          </h3>
          <div className="space-y-3">
            <InputField label="WhatsApp" field="whatsapp_number" description="Número para botón flotante y calculadora" />
            <InputField label="Email de Contacto" field="contact_email" type="email" />
            <InputField label="Teléfono" field="contact_phone" />
          </div>
        </div>

        {/* Redes Sociales */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 lg:col-span-2 xl:col-span-1">
          <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-400" />
            Redes Sociales
          </h3>
          <div className="space-y-3">
            <InputField label="Facebook" field="facebook_url" type="url" />
            <InputField label="Instagram" field="instagram_url" type="url" />
            <InputField label="LinkedIn" field="linkedin_url" type="url" />
            <InputField label="YouTube" field="youtube_url" type="url" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
